<template>
  <div v-if="activeVendor && getUserData" class="menu-wide order-again theme-group-2">
    <scan-and-go-messages v-if="isScanEnabled" />
    <app-page-wrapper-grid class="registration__grid">
      <!-- Back button -->
      <div class="iw-layout-grid__row p-t-24 p-b-10">
        <div class="iw-layout-grid__col--span-12 p-l-16">
          <back-button :route-to="{ name: ROUTE_NAMES.MENU }" />
        </div>
      </div>

      <!-- Title -->
      <div class="iw-layout-grid__row p-t-10 p-b-7">
        <div class="iw-layout-grid__col--span-12 p-l-16">
          <h3 class="iw-page-title">Order Again</h3>
        </div>
      </div>

      <!-- Previous Orders -->
      <template v-if="previousOrders && previousOrders.length">
        <div v-for="order in previousOrders" :key="order.orderId">
          <div v-if="filterPreviusOrder(order)" class="iw-layout-grid__row order-again__previous-order-top-row p-t-8">
            <div class="iw-layout-grid__col--span-12">
              <div class="order-again__previous-order-top">
                <div class="order-again__datetime-wrapper">
                  <span class="order-again__date">{{ getFormattedDate(order.orderDate, order.orderDateTz) }}</span>
                  <span class="order-again__time">{{ getFormattedTime(order.orderDate, order.orderDateTz) }}</span>
                </div>
                <add-all-button :is-disabled="isOrderAllDisabled(order)" @click="tryToOrderAll(order)" />
              </div>
            </div>
          </div>
          <div class="iw-layout-grid__row order-again__items-row">
            <vendor-menu-item
              v-for="(item, i) in getFilteredItems(order.items)"
              :key="`${i}_${order.orderId}_${item.id}`"
              :order-id="order.orderId"
              :menu-item="item"
              :v-if="activeItem(item)"
              :item-count-updated="itemCountUpdated"
            ></vendor-menu-item>
          </div>
        </div>
      </template>

      <!-- No Orders or request error -->
      <template v-else>
        <div class="iw-layout-grid__row p-l-16 p-b-8 p-t-24">
          <div class="iw-layout-grid__col--span-12">
            <template v-if="errorOnRequest">
              <div class="fail-on-request-wrapper">
                <div class="order-again__fail-on-request">
                  <h5>Your orders could not be retrieved.</h5>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="no-items-wrapper">
                <div class="order-again__no-items">
                  <h5>You do not have any available orders.</h5>
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>
    </app-page-wrapper-grid>
  </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import { mapGetters, mapActions } from "vuex";
import VendorMenuItem from "@/routes/menu/components/menu-list/menu-item/VendorMenuItem";
import ConfirmationModal from "@/components/modal/confirmation/ConfirmationModal";
import { DateTime } from "luxon";
import { showDialog, ROUTE_NAMES, OAPI_API_TYPE } from "@/utils";
import BackButton from "@/components/buttons/back-button/BackButton";
import AddAllButton from "@/components/buttons/add-all-button/AddAllButton";

export const ScanAndGoMessages = defineAsyncComponent(() => import("@/components/messages/ScanAndGoMessages"));

export const itemsCountList = {};

export default {
  name: "OrderAgain",
  components: { VendorMenuItem, ScanAndGoMessages, BackButton, AddAllButton },
  data() {
    return {
      noPreviousOrders: "",
      prevRoute: null,
      errorOnRequest: false,
      itemsCountList: itemsCountList,
      ROUTE_NAMES
    };
  },
  computed: {
    ...mapGetters({
      activeVendor: "vendor/getActiveVendor",
      getUserData: "user/getUserData",
      previousOrders: "user/getPreviousOrders",
      isAlcoholEnabled: "vendor/isAlcoholEnabled",
      venueInfo: "venue/getVenueInfo",
      getActiveItems: "vendor/getOrderAgainItems",
      APIType: "venueSettings/APIType",
      isAlcoholLimitReached: "cart/isAlcoholLimitReached",
      alcoholLimitMessagePerOrder: "vendor/getOrderAlcoholLimitMessage"
    }),
    isScanEnabled() {
      return this.APIType === OAPI_API_TYPE && this.activeVendor?.scanToAddToCart;
    }
  },
  beforeUpdate: function () {
    if (!this.activeVendor || !this.getUserData)
      this.$router.push({
        name: ROUTE_NAMES.VENDOR_PICKER
      });
  },
  methods: {
    ...mapActions({
      addItemToCart: "cart/addItemToCart",
      clearDiscount: "cart/clearDiscount"
    }),
    activeItem(item) {
      return item?.status === "STATUS_ACTIVE";
    },
    itemCountUpdated(orderId, itemId, count) {
      if (!itemsCountList[orderId]) itemsCountList[orderId] = {};
      itemsCountList[orderId][itemId] = count;
    },
    getDate(date, timezone) {
      const dateStr = date.split(" ")[0];
      const timeStr = date.split(" ")[1];
      const localDate = DateTime.fromISO(`${dateStr}T${timeStr}.000`).setZone(timezone);
      return DateTime.fromISO(localDate).setZone(this.venueInfo.timezone);
    },
    getFormattedDate(date, timezone) {
      return this.getDate(date, timezone).toFormat("MM/dd/yyyy");
    },
    getFormattedTime(date, timezone) {
      return this.getDate(date, timezone).toFormat("h:mm a");
    },
    filterPreviusOrder(order) {
      const orderLength = order.items.length >= 1;
      const orderAvailability = this.getActiveItems(order.items).length !== 0;
      return orderLength && orderAvailability;
    },
    getFilteredItems(items = []) {
      if (!this.isAlcoholEnabled) {
        const elements = this.getActiveItems(items);
        return elements.filter((item) => !item.isAlcohol);
      }
      return this.getActiveItems(items);
    },

    tryToOrderAll(order) {
      this.orderAll(order);
    },
    orderAll(order) {
      const items = this.getFilteredItems(order.items);
      const length = items.length;
      let item;
      let payload;
      let i = 0;

      for (; i < length; i++) {
        item = items[i];
        if (!item) continue;

        if (item.isAlcohol && this.isAlcoholLimitReached()) {
          return showDialog(this, "Warning", this.alcoholLimitMessagePerOrder);
        }

        payload = {
          item,
          quantity: itemsCountList[order.orderId]?.[item.id] || 1,
          specialInstructions: ""
        };

        this.addItemToCart(payload);

        if (i >= length - 1) {
          this.$modal.show(
            ConfirmationModal,
            {
              message: "Items added to cart"
            },
            {
              name: "Confirmation",
              width: 400,
              alwaysShowBoarderRadius: true,
              transition: "scale",
              clickToClose: true,
              background: "transparent"
            }
          );
        }
      }
    },
    isOrderAllDisabled(order) {
      const items = order.items || [];
      const length = items.length;
      let item;

      for (let i = 0; i < length; i++) {
        item = items[i];
        if (!item) continue;
        if (item.modifierGroups?.length) {
          return true;
        }
      }

      return false;
    }
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (to.params.errorOnRequest) {
        vm.errorOnRequest = true;
      }
      vm.prevRoute = from;
    });
  }
};
</script>

<style lang="scss" scoped>
.order-again {
  .order-again__previous-order-top-row {
    @include respond-to-mobile {
      padding-bottom: 4px;
    }
  }

  &__previous-order-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;

    @include respond-to-mobile {
      margin-right: 8px;
    }
  }

  &__date {
    font-size: 16px;
    font-weight: $font_bold;
    margin-left: 16px;
    color: $group-2-color-2;
    opacity: 0.7;
  }

  &__time {
    font-size: 16px;
    margin-left: 12px;
    color: $group-2-color-2;
    opacity: 0.7;
  }

  &__no-items {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 36px;
    font-weight: 200;
    font-family: $fontFamilySecondary;
    h5 {
      margin: 0;
    }

    @include respond-to-mobile {
      font-size: 1.5em;
    }
  }

  &__fail-on-request {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 36px;
    font-weight: 200;
    font-family: $fontFamilySecondary;
    h5 {
      margin: 0;
    }

    @include respond-to-mobile {
      font-size: 1.5em;
    }
  }

  .order-again__items-row {
    padding-bottom: 24px;

    @include respond-to-mobile {
      padding-bottom: 16px;
    }
  }
}

.no-items-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 60%;
}

.fail-on-request-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 60%;
}
</style>
